/**
 * VendorShipmentsPage — iter171
 *
 * Global list of vendor shipments (= supplier invoices). The label
 * column is the invoice number. Each row links to a detail modal
 * that surfaces:
 *   • The invoice header (date, supplier, amount, notes)
 *   • The Google Drive file (view + download) resolved from the
 *     ``vendor_shipments_folder_id`` root configured by super-admins
 *   • Purchase_Order_Details rows actually shipped on this invoice
 *   • Journal Entry rows tied to this invoice (LEFT JOIN — supplier
 *     payments without a vendor-shipment FK are intentionally
 *     omitted, the user confirmed this is expected)
 *
 * The page mirrors the visual language of SuppliersPage (left-aligned
 * filter bar + table + right-side detail panel) to keep the staff
 * tools cohesive.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Receipt, MagnifyingGlass, FileText, ArrowSquareOut, DownloadSimple, Warning, X } from "@phosphor-icons/react";

import HeaderBrand from "@/components/HeaderBrand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  fetchVendorShipments,
  fetchVendorShipment,
} from "@/lib/api";


function fmtMoney(v, currency) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "—";
  const cur = (currency || "USD").toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency: cur, maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${cur} ${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  }
}


export default function VendorShipmentsPage({ user }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [driveConfigured, setDriveConfigured] = useState(false);

  const [q, setQ] = useState(searchParams.get("q") || "");
  const [supplier, setSupplier] = useState(searchParams.get("supplier") || "");
  const [fromDate, setFromDate] = useState(searchParams.get("from") || "");
  const [toDate, setToDate] = useState(searchParams.get("to") || "");
  const [page, setPage] = useState(0);
  const limit = 50;

  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!user?.email) navigate("/", { replace: true });
  }, [user?.email, navigate]);

  const load = async (overrides = {}) => {
    setBusy(true); setError("");
    try {
      const data = await fetchVendorShipments({
        q,
        supplier,
        from_date: fromDate,
        to_date: toDate,
        limit,
        offset: page * limit,
        ...overrides,
      });
      setRows(data.items || []);
      setTotal(data.total || 0);
      setDriveConfigured(!!data.drive_configured);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Failed to load";
      setError(detail);
      setRows([]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, [page]);
  const applyFilters = () => {
    setPage(0);
    const next = {};
    if (q) next.q = q;
    if (supplier) next.supplier = supplier;
    if (fromDate) next.from = fromDate;
    if (toDate) next.to = toDate;
    setSearchParams(next);
    load();
  };

  const lastPage = Math.max(0, Math.ceil(total / limit) - 1);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="vendor-shipments-page">
      <HeaderBrand user={user} />
      <main className="max-w-[1600px] mx-auto px-4 py-4">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Receipt size={20} weight="bold" className="text-indigo-700" />
            <h1 className="text-lg font-bold text-slate-900">Supplier invoices</h1>
            <span className="text-xs text-slate-500 font-mono"
                    data-testid="vendor-shipments-total">{total} total</span>
          </div>
          {!driveConfigured && (
            <div className="flex items-center gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1 rounded-sm"
                  data-testid="vendor-shipments-drive-warning">
              <Warning size={12} weight="bold" />
              <span>Drive invoice folder not configured.</span>
              <button onClick={() => navigate("/admin/settings")}
                       className="underline font-semibold ml-1">
                Configure
              </button>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-3 bg-white border border-slate-200 rounded-sm p-2">
          <div className="md:col-span-2 relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="vendor-shipments-search"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                      placeholder="Search invoice #, supplier, notes…"
                      className="h-8 text-xs pl-7 rounded-sm" />
          </div>
          <Input data-testid="vendor-shipments-supplier"
                    value={supplier}
                    onChange={(e) => setSupplier(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                    placeholder="Exact supplier company"
                    className="h-8 text-xs rounded-sm" />
          <Input data-testid="vendor-shipments-from"
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="h-8 text-xs rounded-sm" />
          <Input data-testid="vendor-shipments-to"
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="h-8 text-xs rounded-sm" />
          <div className="md:col-span-5 flex justify-end gap-2">
            <Button data-testid="vendor-shipments-reset"
                       onClick={() => {
                         setQ(""); setSupplier(""); setFromDate(""); setToDate("");
                         setPage(0); setSearchParams({});
                         // Pass overrides so the closure sees the
                         // cleared filters instead of the stale state.
                         load({ q: "", supplier: "", from_date: "",
                                  to_date: "", offset: 0 });
                       }}
                       className="h-8 px-3 text-xs rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200">
              Reset
            </Button>
            <Button data-testid="vendor-shipments-apply"
                       onClick={applyFilters}
                       className="h-8 px-3 text-xs rounded-sm bg-indigo-700 hover:bg-indigo-800 text-white">
              Apply filters
            </Button>
          </div>
        </div>

        {error && (
          <div data-testid="vendor-shipments-error"
                className="text-xs px-3 py-2 mb-3 rounded-sm border bg-red-50 border-red-200 text-red-800">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto bg-white border border-slate-200 rounded-sm">
          <table className="w-full text-xs" data-testid="vendor-shipments-table">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="px-3 py-2 font-semibold">Invoice #</th>
                <th className="px-3 py-2 font-semibold">Supplier</th>
                <th className="px-3 py-2 font-semibold">Invoice date</th>
                <th className="px-3 py-2 font-semibold text-right">Total</th>
                <th className="px-3 py-2 font-semibold">Linked PO</th>
                <th className="px-3 py-2 font-semibold">Attachment</th>
              </tr>
            </thead>
            <tbody>
              {busy && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400" data-testid="vendor-shipments-loading">Loading…</td></tr>
              )}
              {!busy && rows.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400" data-testid="vendor-shipments-empty">No invoices match these filters.</td></tr>
              )}
              {!busy && rows.map((r) => (
                <tr key={r.id}
                     data-testid={`vendor-shipment-row-${r.id}`}
                     onClick={() => setSelectedId(r.id)}
                     className={"border-t border-slate-100 hover:bg-indigo-50/50 cursor-pointer "
                                + (selectedId === r.id ? "bg-indigo-50" : "")}>
                  <td className="px-3 py-2 font-mono font-bold text-indigo-700">
                    {r.invoice_number || `#${r.id}`}
                  </td>
                  <td className="px-3 py-2">{r.supplier_company_name || "—"}</td>
                  <td className="px-3 py-2">{r.invoice_date || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtMoney(r.total, r.currency)}</td>
                  <td className="px-3 py-2 font-mono text-slate-600">{r.po_link || "—"}</td>
                  <td className="px-3 py-2 text-slate-500">
                    {r.file_name
                      ? <span className="inline-flex items-center gap-1"><FileText size={11} />{r.file_name}</span>
                      : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!busy && total > limit && (
          <div className="flex items-center justify-end gap-2 mt-2 text-xs">
            <Button data-testid="vendor-shipments-prev"
                       onClick={() => setPage((p) => Math.max(0, p - 1))}
                       disabled={page === 0}
                       className="h-7 px-2 text-xs rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 disabled:opacity-40">
              ←
            </Button>
            <span className="text-slate-500">page {page + 1} / {lastPage + 1}</span>
            <Button data-testid="vendor-shipments-next"
                       onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                       disabled={page >= lastPage}
                       className="h-7 px-2 text-xs rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 disabled:opacity-40">
              →
            </Button>
          </div>
        )}
      </main>

      {selectedId && (
        <VendorShipmentDetailModal
          shipmentId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}


// ─── Detail modal ───────────────────────────────────────────────────
export function VendorShipmentDetailModal({ shipmentId, onClose }) {
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    setBusy(true); setError("");
    fetchVendorShipment(shipmentId)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.detail || e?.message || "Failed to load invoice");
      })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [shipmentId]);

  const head = data?.head;

  // iter192 — operator rule: internal DB keys are NEVER shown in the
  // UI. The former "Show internal IDs" toggle (Detail ID / QB JE #)
  // was removed entirely; staff who still need to trace a row in QBO
  // can query the DB directly.

  const detailRows = useMemo(() => {
    const items = data?.details || [];
    return items.map((row) => {
      // The actual columns on Purchase_Order_Details are:
      // POdetailID, PO_Heading, Invoice Number, Product_Name, Price,
      // Quantity, Line_Total, Status, Supplier, Cost Per Item.
      //
      // iter175 — Product_Name actually stores Parts._ComputedKey
      // (a hex hash), not a human SKU. The backend now LEFT JOINs
      // Parts so we can render the real ``part_name`` (+ Spec1
      // when present) and hide the hash entirely.
      const sku = row.part_name
                  || row.Product_Code || row.ProductCode || row.SKU
                  || row.product_code || row["Product Code"] || "";
      const description = row.part_spec1
                          || row.Description || row["Product Description"]
                          || row.Special_Instructions || row["Special Instructions"]
                          || "";
      const qty = row.Quantity || row.Quantity_Counted || row.Qty
                  || row.quantity || "";
      const unit = row.Price || row["Cost Per Item"] || row["Unit Price"] || "";
      const total = row.Line_Total || row["Line Total"] || row.line_total || "";
      return { detailId: row.detail_id || row.POdetailID || row.ID || row.id,
                 sku, description, qty, unit, total, raw: row };
    });
  }, [data]);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center" data-testid="vendor-shipment-detail-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-5xl bg-white rounded-sm shadow-xl border border-slate-200 max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <Receipt size={16} weight="bold" className="text-indigo-700" />
            <span className="font-bold text-sm text-slate-900">
              Invoice {head?.invoice_number || `#${shipmentId}`}
            </span>
            {head?.supplier_company_name && (
              <span className="text-xs text-slate-500 ml-1">· {head.supplier_company_name}</span>
            )}
          </div>
          <button data-testid="vendor-shipment-detail-close"
                   onClick={onClose}
                   className="w-7 h-7 flex items-center justify-center rounded-sm border border-slate-200 hover:bg-slate-100 text-slate-600">
            <X size={13} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {busy && <div className="text-xs text-slate-400">Loading invoice…</div>}
          {error && (
            <div className="text-xs px-3 py-2 rounded-sm border bg-red-50 border-red-200 text-red-800"
                  data-testid="vendor-shipment-detail-error">
              {error}
            </div>
          )}

          {head && (
            <>
              {/* Header */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <KPI label="Invoice date" value={head.invoice_date || "—"} />
                <KPI label="Total"        value={fmtMoney(head.total, head.currency)} />
                <KPI label="Currency"     value={head.currency || "—"} />
                <KPI label="Linked PO"    value={head.po_link || "—"} mono />
              </div>

              {/* Drive attachments — vendor shipments can carry many
                  scanned documents (Commercial Invoice, Proforma,
                  Packing List, BOL, Broker Invoice, …). We surface
                  every populated attachment column as its own row. */}
              <section data-testid="vendor-shipment-attachments">
                <SectionTitle>
                  Attachments
                  <span className="ml-2 text-[11px] text-slate-400 font-normal">
                    ({(head.attachments || []).length})
                  </span>
                </SectionTitle>
                {(head.attachments || []).length === 0 ? (
                  <div className="text-xs text-slate-400" data-testid="vendor-shipment-attachments-empty">
                    No attachments recorded for this invoice.
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {(head.attachments || []).map((a) => (
                      <div key={a.label}
                            data-testid={`vendor-shipment-attachment-${a.label.replace(/[^a-zA-Z0-9]+/g, "-")}`}
                            className="flex items-center gap-2 flex-wrap bg-slate-50 border border-slate-200 rounded-sm px-2 py-1.5">
                        <FileText size={13} weight="bold" className="text-slate-500" />
                        <span className="text-xs font-semibold text-slate-700">{a.label}</span>
                        <span className="text-[11px] text-slate-500 font-mono break-all">{a.file_path}</span>
                        <div className="flex-1" />
                        {a.drive_view_url ? (
                          <>
                            <a href={a.drive_view_url}
                                target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-sm px-1.5 py-0.5">
                              <ArrowSquareOut size={11} /> View
                            </a>
                            <a href={a.drive_download_url}
                                target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-sm px-1.5 py-0.5">
                              <DownloadSimple size={11} /> Download
                            </a>
                          </>
                        ) : (
                          <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-1.5 py-0.5">
                            not in Drive folder
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {!data?.drive_configured && (
                  <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1 rounded-sm"
                        data-testid="vendor-shipment-drive-not-configured">
                    Drive root folder isn&apos;t configured. <button onClick={onClose} className="underline">Close</button> and visit <span className="font-mono">/admin/settings → Database</span>.
                  </div>
                )}
              </section>

              {head.notes && (
                <section>
                  <SectionTitle>Notes</SectionTitle>
                  <div className="text-xs whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded-sm px-2 py-1.5">
                    {head.notes}
                  </div>
                </section>
              )}

              {/* PO Details (what was shipped) */}
              <section>
                <SectionTitle>
                  Shipped lines
                  <span className="ml-2 text-[11px] text-slate-400 font-normal">
                    ({detailRows.length})
                  </span>
                </SectionTitle>
                {detailRows.length === 0 ? (
                  <div className="text-xs text-slate-400 py-2"
                        data-testid="vendor-shipment-details-empty">
                    No Purchase_Order_Details rows linked to this invoice.
                  </div>
                ) : (
                  <div className="overflow-x-auto border border-slate-200 rounded-sm">
                    <table className="w-full text-xs" data-testid="vendor-shipment-details-table">
                      <thead className="bg-slate-50 text-left text-slate-600">
                        <tr>
                          <th className="px-2 py-1.5 font-semibold">Part</th>
                          <th className="px-2 py-1.5 font-semibold">Description</th>
                          <th className="px-2 py-1.5 font-semibold text-right">Qty</th>
                          <th className="px-2 py-1.5 font-semibold text-right">Unit</th>
                          <th className="px-2 py-1.5 font-semibold text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailRows.map((d) => (
                          <tr key={d.detailId}
                               data-testid={`vendor-shipment-detail-row-${d.detailId}`}
                               className="border-t border-slate-100">
                            <td className="px-2 py-1.5 font-semibold text-slate-800">{d.sku || "—"}</td>
                            <td className="px-2 py-1.5 text-slate-600">{d.description || "—"}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{d.qty || "—"}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{d.unit ? fmtMoney(d.unit, head.currency) : "—"}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{d.total ? fmtMoney(d.total, head.currency) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* Journal Entries */}
              <section>
                <SectionTitle>
                  Journal entries
                  <span className="ml-2 text-[11px] text-slate-400 font-normal">
                    ({data?.journal?.length || 0})
                  </span>
                </SectionTitle>
                {(data?.journal || []).length === 0 ? (
                  <div className="text-xs text-slate-400 py-2"
                        data-testid="vendor-shipment-journal-empty">
                    No journal entries reference this invoice. (Consolidated supplier payments without a vendor-shipment FK won&apos;t show up here.)
                  </div>
                ) : (
                  <div className="overflow-x-auto border border-slate-200 rounded-sm">
                    <table className="w-full text-xs" data-testid="vendor-shipment-journal-table">
                      <thead className="bg-slate-50 text-left text-slate-600">
                        <tr>
                          <th className="px-2 py-1.5 font-semibold">Type</th>
                          <th className="px-2 py-1.5 font-semibold">Date</th>
                          <th className="px-2 py-1.5 font-semibold">Status</th>
                          <th className="px-2 py-1.5 font-semibold text-right">Debit</th>
                          <th className="px-2 py-1.5 font-semibold text-right">Credit</th>
                          <th className="px-2 py-1.5 font-semibold">Reference</th>
                          <th className="px-2 py-1.5 font-semibold">Explanation</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data?.journal || []).map((j) => (
                          <tr key={j.ID}
                               data-testid={`vendor-shipment-journal-row-${j.ID}`}
                               className="border-t border-slate-100">
                            <td className="px-2 py-1.5">{j.je_type || "—"}</td>
                            <td className="px-2 py-1.5">{j.je_date || "—"}</td>
                            <td className="px-2 py-1.5">{j.Status || "—"}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-emerald-700">{j.Debit ? fmtMoney(j.Debit, head.currency) : "—"}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-rose-700">{j.Credit ? fmtMoney(j.Credit, head.currency) : "—"}</td>
                            <td className="px-2 py-1.5 text-slate-600 font-mono">{j.Reference || "—"}</td>
                            <td className="px-2 py-1.5 text-slate-600">{j.Explanation || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function KPI({ label, value, mono }) {
  return (
    <div className="border border-slate-200 rounded-sm px-2 py-1.5 bg-white">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={"text-sm text-slate-900 " + (mono ? "font-mono" : "font-semibold")}>{value}</div>
    </div>
  );
}


function SectionTitle({ children }) {
  return (
    <h3 className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1.5 flex items-center gap-1">
      {children}
    </h3>
  );
}
